import { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'
import { toast } from 'sonner'
import { MapPin, UserRound, Phone, Mail, AlertTriangle, ExternalLink } from 'lucide-react'
import type {
  ProjectDetailDto, EmployeeDto, AlertAnchorStep, ProjectDetailAlertDto,
  SurveyDto, InstallationDto, ScheduleEventDto, ConfirmationChannel, CreateEventBody,
  PlanningSiteAddressDto, WorkItemStatus, InterventionNature,
} from '../../../../lib/api/v2/planning'
import * as planningApi from '../../../../lib/api/v2/planning'
import { ApiError } from '../../../../lib/api/client'
import { formatDate } from '../../../../lib/format'
import { poseBudget, slotsOfPose, slotsOfSurvey, formatDuration, poseAcceptsNewSlot, surveyAcceptsNewSlot, surveyAcceptsNotApplicable, hasUpcomingSlot } from '../work-item-slots'
import { cn } from '../../../../lib/utils'
import { formatEventWhen, formatCalendarDay, assignableOnly, isWorkItemSettled, slotHasEnded } from '../planning-helpers'
import { WorkItemSlotCard, type SlotForm, type RescheduleBody } from './WorkItemSlotCard'
import { HistoryAccordion } from './HistoryAccordion'
import { SupplierOrders } from './SupplierOrders'
import { SectionCard } from './SectionCard'
import { deriveSectionStatus, type SectionTone } from './deriveSectionStatus'
import { deriveProjectSummary } from './deriveProjectSummary'
import { deriveCommandesPhase } from './supplier-orders-phase'
import { readyCheckLines } from './readyCheckLines'
import { ReportForm, ReasonForm, EstimateForm, NotesForm, ScheduleForm } from './ProjectDetailForms'
import { ConstraintsBlock, sectionHasConstraintAlert } from './ConstraintsBlock'
import { ReportArea } from './ReportArea'
import { computeStatusCounts, readiness } from './supplier-orders-helpers'
import { InterventionNatureBadge } from '../InterventionNatureBadge'
import { AddInterventionDialog } from '../AddInterventionDialog'

/** Which inline form is open in the Métré section (one at a time). */
/**
 * Forms that belong to the Métré SECTION rather than to one of its créneaux — SOLY-PLAN-006 MS-04,
 * the exact split SOLY-PLAN-003 MS-11 made for the pose.
 *
 * `confirm` / `cancel-event` / `assign` left this list for the same reason they left the pose's:
 * they act on a créneau, and a métré now has N of them — a section-level « confirmer » would have
 * to pick one silently, which is precisely the ambiguity multi-créneaux introduces. `schedule` is
 * the FIRST passage (no créneau yet), `add-slot` one more.
 */
type MetreSectionForm = null | 'schedule' | 'add-slot' | 'report' | 'estimate' | 'na'

type MetreFormState =
  | null
  | { kind: 'section'; form: MetreSectionForm }
  | { kind: 'slot'; slotId: string; form: Exclude<SlotForm, null> }

/**
 * Forms that belong to the Pose SECTION rather than to one of its créneaux.
 *
 * `confirm` / `cancel-event` / `assign` left this list in SOLY-PLAN-003 MS-11: they act on a
 * créneau, and a pose has N of them — a section-level « confirmer » would have to silently pick
 * one, which is exactly the ambiguity multi-créneaux introduced. `schedule` is the FIRST
 * scheduling (no créneau yet), `add-slot` one more.
 */
type PoseSectionForm = 'schedule' | 'add-slot' | 'report' | 'estimate' | 'na' | 'notes'

/**
 * The single open form of a Pose section — one at a time, whether it hangs off the section or off
 * one créneau. Two independent states would let a section form and a card form be open at once,
 * with two « Annuler » buttons doing different things on the same screen.
 */
type PoseFormState =
  | null
  | { kind: 'section'; form: PoseSectionForm }
  | { kind: 'slot'; slotId: string; form: Exclude<SlotForm, null> }

interface ProjectDetailProps {
  projectId: string
  onMutated: () => void
  /** Fired once the detail has loaded, with the project's planned pose date (yyyy-MM-dd) or null —
   *  lets the cockpit jump the agenda to the pose on open. */
  onLoaded?: (poseStartIso: string | null) => void
  userNameById?: Record<string, string>
  employees?: EmployeeDto[]
  /** Open anchored to a section (from a Suivi alert): scroll it into view + expand Commandes. */
  anchorStep?: AlertAnchorStep
  /**
   * Show a créneau where it actually sits — SOLY-PLAN-003 MS-12. Receives the whole slot, not its
   * id: the cockpit lists créneaux that may be OUTSIDE the agenda's loaded window, and looking the
   * id up there would do nothing on exactly the ones this gesture exists for.
   */
  onFocusSlot?: (slot: ScheduleEventDto) => void
}

/** Tone → liseré for the Commandes section (no work-item state machine there — derived from alerts). */
function commandesTone(alerts: ProjectDetailAlertDto[], allReceived: boolean): { tone: 'gris' | 'ambre' | 'rouge' | 'vert'; label: string | null; count?: number } {
  if (alerts.some((a) => a.severity === 'Hard')) return { tone: 'rouge', label: 'À régler', count: alerts.length }
  if (alerts.some((a) => a.severity === 'Soft')) return { tone: 'ambre', label: 'À vérifier', count: alerts.length }
  if (allReceived) return { tone: 'vert', label: 'Reçues' }
  return { tone: 'gris', label: null }
}

/** Small sober tone dot for the header summary line (no fills — just a coloured dot). */
const SUMMARY_DOT: Record<SectionTone, string> = {
  gris: 'bg-muted-foreground/50',
  ambre: 'bg-amber-500',
  rouge: 'bg-red-500',
  vert: 'bg-emerald-500',
}

/** One-line job-site address, e.g. "Rue de la Gare 12/B, 1400 Nivelles". */
function formatSiteAddress(a: PlanningSiteAddressDto): string {
  const line1 = [a.street, a.number].filter(Boolean).join(' ') + (a.box ? `/${a.box}` : '')
  const line2 = [a.postalCode, a.city].filter(Boolean).join(' ')
  return [line1.trim(), line2.trim()].filter(Boolean).join(', ')
}

/**
 * True once the event (RDV) is over at Brussels now — gates "Terminer + rapport". Its end, not its
 * start: see slotHasEnded, which mirrors the backend's HasEnded.
 */
function eventIsPast(event: ScheduleEventDto | undefined): boolean {
  return !!event && slotHasEnded(event)
}

/** Chronological key of a slot's start — its day, then its wall clock (a Days slot heads its day). */
const startKey = (e: ScheduleEventDto) => `${e.startDate} ${e.startTime ?? ''}`

/**
 * One labelled field row (Round 5 §lignes-champ): « Label · valeur (ou non défini ⚠) · action discrète ».
 * The amber « non défini » fallback fires when `defined` is false, surfacing at a glance what's missing.
 */
function FieldRow({ label, defined, value, action, testId, undefinedLabel = 'non défini', optional = false }: {
  label: string
  defined: boolean
  value: ReactNode
  action?: { label: string; testId: string; onClick: () => void }
  testId?: string
  /** Fallback text when `defined` is false (e.g. « non planifié » for a date). */
  undefinedLabel?: string
  /** Optional field (e.g. Notes) : an empty value is NOT a problem → neutral grey, no amber warning. */
  optional?: boolean
}) {
  return (
    <div className="flex items-baseline gap-2 py-1.5 text-xs" data-testid={testId}>
      <span className="w-24 flex-none text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1">
        {defined ? (
          <span className="font-medium text-foreground">{value}</span>
        ) : optional ? (
          <span className="text-muted-foreground">{undefinedLabel}</span>
        ) : (
          <span className="inline-flex items-center gap-1 font-medium text-amber-600">
            <AlertTriangle className="h-3 w-3 flex-none" aria-hidden /> {undefinedLabel}
          </span>
        )}
      </span>
      {action && (
        <button type="button" data-testid={action.testId} onClick={action.onClick}
          className="flex-none text-[11px] font-medium text-primary hover:underline">
          {action.label}
        </button>
      )}
    </div>
  )
}

/** Terminal « non nécessaire » state: a clear line + the reason + a way back (Réactiver → à planifier). */
function TerminalNotApplicable({ area, kind, reason, onReactivate, readOnly = false }: {
  area: 'metre' | 'pose'
  kind: 'survey' | 'installation'
  reason: string | null
  onReactivate: () => void
  readOnly?: boolean
}) {
  const label = kind === 'survey' ? 'Métré marqué non nécessaire' : 'Pose marquée non nécessaire'
  return (
    <div className="flex items-baseline justify-between gap-2 rounded-md bg-muted/40 p-2 text-xs" data-testid={`planning-${area}-notapplicable`}>
      <span className="min-w-0 text-muted-foreground">
        <span className="font-medium text-foreground">{label}</span>
        {reason && <span className="block">{reason}</span>}
      </span>
      {!readOnly && (
        <button type="button" data-testid={`planning-${area}-reactivate-btn`} onClick={onReactivate}
          className="flex-none text-[11px] font-medium text-primary hover:underline">Réactiver</button>
      )}
    </div>
  )
}

/**
 * Phase « terminé » (Completed) recap (mini-scope 29): a single read-only line « Réalisé le {date} · {qui} »
 * above the report. Keeps the who/when traceable at a glance once the section is locked; the report itself
 * is rendered by <ReportArea> below. Rendered only when the live RDV is still known.
 */
function CompletedRecap({ area, event, who }: {
  area: 'metre' | 'pose'
  event: ScheduleEventDto | undefined
  who: string | undefined
}) {
  if (!event) return null
  return (
    <p className="py-1.5 text-xs text-muted-foreground" data-testid={`planning-${area}-done-recap`}>
      Réalisé le <span className="font-medium text-foreground">{formatEventWhen(event)}</span>
      {who ? <> · {who}</> : null}
    </p>
  )
}

/**
 * The ready-check list (✓ ok · ! missing). Mini-scope 29: ALWAYS the full list — never collapsed to a
 * « Tout est prêt » one-liner. When all green it's the proof everything is ready AND a record of what was
 * done to get there; when something's missing it's the to-do. The « Délais à respecter » block next to it
 * is hidden once ready (handled by the caller).
 */
function ReadyCheckDetail({ kind, missing }: { kind: 'survey' | 'installation'; missing: string[] }) {
  const lines = readyCheckLines(kind, missing)
  return (
    <ul className="mt-2 space-y-1 rounded-md border bg-muted/30 p-2">
      {lines.map((l) => (
        <li key={l.key} className="flex items-center gap-2 text-xs">
          <span className={cn('flex h-4 w-4 flex-none items-center justify-center rounded-full text-[10px] font-bold',
            l.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
            {l.ok ? '✓' : '!'}
          </span>
          <span className={l.ok ? 'text-muted-foreground' : 'font-medium text-foreground'}>{l.label}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Project detail body (SOLY-PLAN-001 MS-23, design Round 4 §Axe B). Three user-first sections
 * (Commandes · Métré · Pose) in style B (4px liseré, discreet derived status), with progressive
 * disclosure: calm = one factual line; à vérifier/à régler = the point(s) + action, full ready-check
 * collapsed behind « voir le détail »; prêt/effectué = a single serene line. Replaces the MS-22
 * readiness bar + 5-point checklist. Mounted by <ProjectDetailDrawer> (the URL-driven cockpit drawer).
 *
 * Action buttons are kept as-is from MS-22 (window.prompt) and merely relocated under the right
 * section — they get replaced by inline forms in MS-24.
 */
export function ProjectDetail({ projectId, onMutated, onLoaded, userNameById = {}, employees = [], anchorStep, onFocusSlot }: ProjectDetailProps) {
  const { can, reasonIfDenied } = usePermissions()
  const [detail, setDetail] = useState<ProjectDetailDto | null>(null)
  const [reminders, setReminders] = useState<planningApi.PlanningReminderDto[]>([])
  const [deliveryLeadDays, setDeliveryLeadDays] = useState<number | null>(null)
  const [confirmDelayDays, setConfirmDelayDays] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  // Inline forms keyed by work item id (SOLY-FINITION-001: one Métré/Pose section per phase).
  const [metreForms, setMetreForms] = useState<Record<string, MetreFormState>>({})
  const [poseForms, setPoseForms] = useState<Record<string, PoseFormState>>({})
  const [addInterventionOpen, setAddInterventionOpen] = useState(false)

  // Section anchors keyed by the Suivi alert anchorStep, so an alert click can scroll to the point.
  const sectionRefs = {
    Metre: useRef<HTMLDivElement>(null),
    Pose: useRef<HTMLDivElement>(null),
    Rapport: useRef<HTMLDivElement>(null),
    Commandes: useRef<HTMLDivElement>(null),
  }

  // Reminders are a side panel of the section — their failure (or an unstubbed test mock)
  // must never block the detail itself.
  const fetchReminders = async (): Promise<planningApi.PlanningReminderDto[]> => {
    try { return await planningApi.getReminders(projectId) } catch { return [] }
  }

  // Effective C7 lead for the inline send/confirm warning + the org confirmation-delay default
  // (UX r2 arbitrage: supplier delay > org KV > 5) — refetched on EVERY load() so an edit in the
  // Délais block below shows the same numbers immediately (review B1, cohérence amont).
  const fetchOrderRules = async (): Promise<{ deliveryLead: number | null; confirmDelay: number | null }> => {
    try {
      const dto = await planningApi.getProjectConstraints(projectId)
      const days = (key: string) => {
        const rule = dto.rules.find((r) => r.key === key)
        return rule && !rule.dismissed ? rule.days : null
      }
      return { deliveryLead: days('DeliveryBeforePoseLeadDays'), confirmDelay: days('SupplierConfirmDelayDays') }
    } catch { return { deliveryLead: null, confirmDelay: null } }
  }

  // Silent refresh after a mutation: no setLoading(true) — the placeholder would unmount the whole
  // tree and destroy local form state (e.g. the « relance n°N+1 » chain proposal opened by « Fait »).
  // The initial fetch (effect below) owns the loading placeholder.
  const load = async () => {
    try {
      const [d, r, rules] = await Promise.all([planningApi.getProjectDetail(projectId), fetchReminders(), fetchOrderRules()])
      setDetail(d)
      setReminders(r)
      setDeliveryLeadDays(rules.deliveryLead)
      setConfirmDelayDays(rules.confirmDelay)
      setNotFound(false)
    }
    catch (e) {
      if (e instanceof ApiError && e.code === 'PROJECT_NOT_FOUND') setNotFound(true)
      else toast.error('Impossible de charger le chantier')
    }
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setNotFound(false)
    void (async () => {
      try {
        const [d, r, rules] = await Promise.all([planningApi.getProjectDetail(projectId), fetchReminders(), fetchOrderRules()])
        if (active) { setDetail(d); setReminders(r); setDeliveryLeadDays(rules.deliveryLead); setConfirmDelayDays(rules.confirmDelay) }
      } catch (e) {
        if (!active) return
        if (e instanceof ApiError && e.code === 'PROJECT_NOT_FOUND') setNotFound(true)
        else toast.error('Impossible de charger le chantier')
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [projectId])

  // Once loaded, scroll the anchored section into view (alert deep-link).
  useEffect(() => {
    if (loading || !detail || !anchorStep) return
    const el = sectionRefs[anchorStep]?.current
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, detail, anchorStep])

  // Report the planned pose date up to the cockpit once loaded (drives the agenda jump on open).
  // Multi-pose (SOLY-FINITION-001): jump to the NEXT upcoming pose across phases; when everything
  // is in the past, fall back to the lowest-phase scheduled pose (mono-pose behavior unchanged).
  useEffect(() => {
    if (!detail) return
    const scheduled = [...detail.installations]
      .sort((a, b) => a.phase - b.phase)
      .map((i) => (i.currentEventId ? detail.events.find((e) => e.id === i.currentEventId) : undefined))
      .filter((e): e is ScheduleEventDto => !!e)
    const upcoming = scheduled.filter((e) => !eventIsPast(e))
    const pick = upcoming.length > 0
      ? upcoming.reduce((a, b) => (startKey(a) <= startKey(b) ? a : b))
      : scheduled[0]
    onLoaded?.(pick ? pick.startDate : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail])

  // Inline-form submit: run the mutation, close every open form, refetch. Errors are surfaced as
  // a toast and re-thrown so the form's own busy-state resets and it stays open for a retry.
  //
  // The close belongs HERE and nowhere else. It happens the instant the mutation returns, before
  // the refetch, so no form is left standing over stale data. A caller that also closes on the
  // returned promise (`.then(close)`) closes a SECOND time, once the refetch has landed -- and by
  // then the user may have opened another form, which that late write destroys under their
  // fingers. TC-032 caught it in CI, where the refetch is slow enough for the gap to open: the
  // confirmation form was unmounted between the channel tap and the submit tap.
  const submit = async (action: () => Promise<unknown>, ok: string) => {
    try {
      await action()
      toast.success(ok)
      setMetreForms({}); setPoseForms({})
      await load(); onMutated()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action échouée')
      throw e
    }
  }

  if (notFound) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center text-sm text-muted-foreground" data-testid="planning-detail-notfound">
        <span className="text-2xl">🔍</span>
        <p className="mt-2 font-medium text-foreground">Projet introuvable</p>
        <p className="mt-1">Ce chantier n'existe pas ou n'est pas accessible.</p>
      </div>
    )
  }

  if (loading || !detail) {
    return <div className="p-4 text-sm text-muted-foreground" data-testid="planning-detail-loading">Chargement…</div>
  }

  // Multi-pose (SOLY-FINITION-001): one Métré/Pose section per work item, ordered by phase.
  const surveys = [...detail.technicalSurveys].sort((a, b) => a.phase - b.phase)
  const installations = [...detail.installations].sort((a, b) => a.phase - b.phase)
  // Phase-1 refs drive the header at-a-glance summary (unchanged from the mono-pose drawer).
  const survey = surveys.find((s) => s.phase === 1)
  const installation = installations.find((i) => i.phase === 1)
  // Resolve the live RDV by the work item's currentEventId ref (mini-scope 28: the single source of
  // truth for « planifié »). Falls back to undefined when « à planifier ».
  const eventOf = (w: { currentEventId: string | null } | undefined): ScheduleEventDto | undefined =>
    w?.currentEventId ? detail.events.find((e) => e.id === w.currentEventId) : undefined
  const installEvent = eventOf(installation)
  const surveyEvent = eventOf(survey)
  // "Terminer + rapport" only shows once the RDV is in the past (design Round 4 §panneau détail).
  const surveyEventPast = eventIsPast(surveyEvent)
  const installEventPast = eventIsPast(installEvent)
  // Next upcoming pose across phases — feeds the Commandes deadline hint (mono-pose: identical).
  const upcomingPoseEvents = installations.map(eventOf).filter((e): e is ScheduleEventDto => !!e && !eventIsPast(e))
  const nextPoseEvent = upcomingPoseEvents.length > 0
    ? upcomingPoseEvents.reduce((a, b) => (startKey(a) <= startKey(b) ? a : b))
    : undefined

  // Alerts filtered per section. Rapport alerts belong to their work item's own section, routed by
  // the BE-declared workItem (_constraints.md invariant 2 — no code-number pattern-matching).
  const metreAlerts = detail.alerts.filter((a) => a.anchorStep === 'Metre' || (a.anchorStep === 'Rapport' && a.workItem === 'TechnicalSurvey'))
  const poseAlerts = detail.alerts.filter((a) => a.anchorStep === 'Pose' || (a.anchorStep === 'Rapport' && a.workItem === 'Installation'))
  const commandesAlerts = detail.alerts.filter((a) => a.anchorStep === 'Commandes')
  // Alert DTOs carry a workItem KIND but no phase/id, so they cannot be attributed to a specific
  // phase. They land on the first still-active section of their kind (else the last one) — with a
  // single work item per kind (the common case) this is exactly the previous behavior.
  const alertTarget = <T extends { id: string; status: WorkItemStatus }>(items: T[]): string | undefined =>
    (items.find((w) => w.status !== 'Completed' && w.status !== 'NotApplicable' && w.status !== 'Cancelled') ?? items[items.length - 1])?.id
  const metreAlertTargetId = alertTarget(surveys)
  const poseAlertTargetId = alertTarget(installations)

  // Reconciliation prompt at pose closure (SOLY-FINITION-001): lines still expected (cancelled
  // excluded) — computed in memory from the already-loaded drawer lines.
  const lineCounts = readiness(computeStatusCounts(detail.supplierOrderLines))
  const unreceivedLineCount = lineCounts.total - lineCounts.received

  // MS-02: cancelled lines (out-of-stock swaps) don't block readiness — mirror the BE ready-check.
  const liveLines = detail.supplierOrderLines.filter((l) => l.lineStatus !== 'Cancelled')
  const allReceived = liveLines.length > 0 && liveLines.every((l) => l.lineStatus === 'Received')
  // Phase drives the header one-liner label (« À définir » on a fresh project, not « En cours »).
  const commandesPhase = deriveCommandesPhase(detail.supplierOrderLines, detail.supplierOrders, detail.nothingToOrderAt)

  // La cellule « Commandes » de l'entête suit exactement la section qu'elle résume : sans
  // `orders:read`, la section n'est pas rendue, donc l'annoncer (« Commandes: À régler ») pointerait
  // vers quelque chose qui n'existe pas plus bas. Le rôle Terrain de la grille §2 est précisément
  // dans ce cas.
  const canReadOrders = can(PERMISSIONS.ORDERS_READ)
  const summaryCells = deriveProjectSummary({
    survey, installation, metreAlerts, poseAlerts, commandesAlerts, commandesPhase,
    surveyEventPast, installEventPast,
    // Le bandeau lit la MÊME règle que la section — encore faut-il lui donner la même entrée.
    surveyHasUpcomingSlot: survey ? hasUpcomingSlot(slotsOfSurvey(detail.events, survey.id)) : false,
  }).filter((c) => c.key !== 'commandes' || canReadOrders)

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="planning-detail">
      <div className="border-b p-3">
        {/* Titre + accès rapides au chantier / client (nouvel onglet — garde le cockpit ouvert). */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold">{detail.clientName}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {detail.reference && <span className="font-mono">{detail.reference}</span>}
              {detail.reference && detail.projectName ? ' · ' : ''}
              {detail.projectName}
            </p>
          </div>
          <div className="flex flex-none flex-col items-stretch gap-1">
            <a href={`/projects/${detail.projectId}`} target="_blank" rel="noopener noreferrer"
              data-testid="planning-detail-open-project"
              className="inline-flex items-center justify-center gap-1 rounded border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted">
              Chantier <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
            {detail.clientId && (
              <a href={`/clients/${detail.clientId}`} target="_blank" rel="noopener noreferrer"
                data-testid="planning-detail-open-client"
                className="inline-flex items-center justify-center gap-1 rounded border px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted">
                Client <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            )}
          </div>
        </div>

        {/* Infos génériques — section claire et à part (adresse chantier, commercial, contacts, signature). */}
        <div className="mt-2 space-y-1 rounded-md border bg-muted/30 p-2 text-xs text-muted-foreground">
          {detail.siteAddress && (
            <p className="flex items-start gap-1.5" data-testid="planning-detail-address">
              <MapPin className="mt-0.5 h-3 w-3 flex-none" aria-hidden />
              <span>{formatSiteAddress(detail.siteAddress)}</span>
            </p>
          )}
          {detail.salesOwnerName && (
            <p className="flex items-center gap-1.5" data-testid="planning-detail-owner">
              <UserRound className="h-3 w-3 flex-none" aria-hidden />
              <span>{detail.salesOwnerName} <span className="text-muted-foreground/60">· commercial</span></span>
            </p>
          )}
          {(detail.contacts ?? []).map((c, i) => (
            <p key={i} className="flex flex-wrap items-center gap-1.5" data-testid="planning-detail-contact">
              <Phone className="h-3 w-3 flex-none" aria-hidden />
              <span className="font-medium text-foreground">{c.name || 'Contact'}</span>
              {c.isPrimary && <span className="text-muted-foreground/60">· principal</span>}
              {c.phone && <a href={`tel:${c.phone}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{c.phone}</a>}
              {c.email && (
                <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:underline" onClick={(e) => e.stopPropagation()}>
                  <Mail className="h-3 w-3 flex-none" aria-hidden />{c.email}
                </a>
              )}
            </p>
          ))}
          {detail.signedAt && <p className="text-muted-foreground/80">Signé le {formatDate(detail.signedAt)}</p>}
        </div>

        {/* État projet en un coup d'œil (dots) */}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="planning-detail-summary">
          {summaryCells.map((c, i) => (
            <span key={c.key} className="inline-flex items-center gap-1.5">
              {i > 0 && <span className="mr-1.5 text-muted-foreground/40" aria-hidden>·</span>}
              <span className={cn('h-1.5 w-1.5 flex-none rounded-full', SUMMARY_DOT[c.tone])} aria-hidden />
              <span>{c.title}: <span className="font-medium text-foreground">{c.label}</span></span>
            </span>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-auto bg-muted/20 p-3">
        {/* ── Commandes ───────────────────────────────────────────────── */}
        {/* Le conteneur d'ancre suit la même garde : sans `orders:read` il ne reste sinon qu'un
            <div> vide, et un clic sur une alerte ancrée « Commandes » scrolle sur rien. */}
        {canReadOrders && <div ref={sectionRefs.Commandes}>
          {(() => {
            const c = commandesTone(commandesAlerts, allReceived)
            return (
              <SectionCard testId="planning-section-commandes" statusTestId="planning-section-commandes-status"
                title="Commandes" tone={c.tone} statusLabel={c.label} count={c.count}>
                <SupplierOrders readOnly={!can(PERMISSIONS.ORDERS_MANAGE)}
                  projectId={projectId} lines={detail.supplierOrderLines} orders={detail.supplierOrders}
                  nothingToOrderAt={detail.nothingToOrderAt} reminders={reminders}
                  poseStartIso={nextPoseEvent ? nextPoseEvent.startDate : null}
                  deliveryLeadDays={deliveryLeadDays} confirmDelayDays={confirmDelayDays}
                  commandesAlerts={commandesAlerts}
                  onMutated={() => { void load(); onMutated() }} />
                {/* « Délais à respecter » pour les commandes : visible uniquement quand une contrainte
                    commandes alerte réellement (mirror Métré/Pose) — caché au stade frais / commande saine. */}
                {sectionHasConstraintAlert(commandesAlerts) && (
                  <div className="mt-2">
                    <ConstraintsBlock readOnly={!can(PERMISSIONS.PLANNING_MANAGE)}
                      projectId={projectId} section="commandes" alerts={commandesAlerts} onMutated={() => { void load(); onMutated() }} />
                  </div>
                )}
              </SectionCard>
            )
          })()}
        </div>}

        {/* ── Métré — one section per phase (SOLY-FINITION-001) ─────────── */}
        {surveys.map((s, idx) => {
          // SOLY-PLAN-006 MS-04 — a métré is a SET of créneaux, read the same way as a pose's.
          const sSlots = slotsOfSurvey(detail.events, s.id)
          const sEvent = sSlots.at(-1)
          const sEventPast = eventIsPast(sEvent)
          const sAlerts = s.id === metreAlertTargetId ? metreAlerts : []
          return (
            <div
              key={s.id}
              ref={idx === 0 ? sectionRefs.Metre : undefined}
              // Phase 1 keeps the historical single-section markup; interventions get a scoping container.
              data-testid={s.phase >= 2 ? `planning-metre-section-${s.phase}` : undefined}
            >
              <MetreSection
                projectId={projectId} onMutated={() => { void load(); onMutated() }}
                survey={s} alerts={sAlerts} surveyEvent={sEvent} surveyEventPast={sEventPast}
                slots={sSlots}
                employees={employees} userNameById={userNameById}
                form={metreForms[s.id] ?? null} onOpenForm={(f) => setMetreForms((prev) => ({ ...prev, [s.id]: f }))}
                onSchedule={(body) => submit(() => planningApi.createEvent(body), 'Créneau planifié')}
                /* The target comes from the CARD that raised the gesture, never from the section's
                   « current » event — a métré has as many passages as it needs. That is also what
                   closes the Codex P1 trap: no shared « selected slot » to go stale. */
                onReschedule={(eventId, body) => submit(() => planningApi.rescheduleEvent(eventId, body), 'Créneau replanifié')}
                onCancelEvent={(eventId, r) => submit(() => planningApi.cancelEvent(eventId, r), 'Créneau annulé')}
                onConfirm={(eventId, channel, note) => submit(() => planningApi.confirmEvent(eventId, channel, note), 'Date confirmée')}
                onUnconfirm={(eventId) => submit(() => planningApi.unconfirmEvent(eventId), 'Créneau remis en proposé')}
                onReport={(sum) => submit(() => planningApi.surveyReport(s.id, projectId, sum), 'Rapport enregistré')}
                onEstimate={(hours, reason) => submit(() => planningApi.surveyEstimate(s.id, projectId, hours, reason), 'Estimé mis à jour')}
                onNotApplicable={(r) => submit(() => planningApi.surveyNotApplicable(s.id, projectId, r), 'Marqué non nécessaire')}
                onReactivate={() => submit(() => planningApi.surveyReactivate(s.id, projectId), 'Métré réactivé')}
                onAssign={(eventId, uid) => { void submit(() => planningApi.assignEvent(eventId, uid), 'Assigné') }}
                onUnassign={(eventId, uid) => { void submit(() => planningApi.unassignEvent(eventId, uid), 'Désassigné') }}
                onSetSlotNote={(eventId, note) => submit(() => planningApi.setEventNote(eventId, note), 'Note enregistrée')}
                onFocusSlot={onFocusSlot}
              />
            </div>
          )
        })}

        {/* ── Pose — one section per phase (SOLY-FINITION-001) ──────────── */}
        {installations.map((inst, idx) => {
          const iEvent = eventOf(inst)
          const iEventPast = eventIsPast(iEvent)
          const iAlerts = inst.id === poseAlertTargetId ? poseAlerts : []
          return (
            <div key={inst.id} ref={idx === 0 ? sectionRefs.Pose : undefined} data-testid={`planning-pose-section-${inst.phase}`}>
              <PoseSection
                projectId={projectId} onMutated={() => { void load(); onMutated() }}
                installation={inst} alerts={iAlerts} installEventPast={iEventPast}
                installEvent={iEvent} slots={slotsOfPose(detail.events, inst.id)}
                nature={inst.nature} unreceivedLineCount={can(PERMISSIONS.ORDERS_READ) ? unreceivedLineCount : 0}
                userNameById={userNameById} employees={employees}
                form={poseForms[inst.id] ?? null} onOpenForm={(f) => setPoseForms((prev) => ({ ...prev, [inst.id]: f }))}
                onSchedule={(body) => submit(() => planningApi.createEvent(body), 'Créneau planifié')}
                /* MS-11: the target comes from the card that raised the gesture, not from the
                   section's « current » event — a pose has as many créneaux as it needs. */
                onReschedule={(eventId, body) => submit(() => planningApi.rescheduleEvent(eventId, body), 'Créneau replanifié')}
                onCancelEvent={(eventId, r) => submit(() => planningApi.cancelEvent(eventId, r), 'Créneau annulé')}
                onConfirm={(eventId, channel, note) => submit(() => planningApi.confirmEvent(eventId, channel, note), 'Date confirmée')}
                onUnconfirm={(eventId) => submit(() => planningApi.unconfirmEvent(eventId), 'Créneau remis en proposé')}
                onReport={(s) => submit(() => planningApi.installationReport(inst.id, projectId, s), 'Rapport enregistré')}
                onEstimate={(body) => submit(() => planningApi.installationEstimate(inst.id, projectId, body), 'Estimé mis à jour')}
                onNotApplicable={(r) => submit(() => planningApi.installationNotApplicable(inst.id, projectId, r), 'Marqué non nécessaire')}
                onReactivate={() => submit(() => planningApi.installationReactivate(inst.id, projectId), 'Pose réactivée')}
                onNotes={(n) => submit(() => planningApi.installationNotes(inst.id, projectId, n), 'Notes enregistrées')}
                onAssign={(eventId, uid) => { void submit(() => planningApi.assignEvent(eventId, uid), 'Assigné') }}
                onUnassign={(eventId, uid) => { void submit(() => planningApi.unassignEvent(eventId, uid), 'Désassigné') }}
                onSetSlotNote={(eventId, note) => submit(() => planningApi.setEventNote(eventId, note), 'Note enregistrée')}
                onFocusSlot={onFocusSlot}
              />
            </div>
          )
        })}

        {/* ── Seconde intervention (SOLY-FINITION-001) — only once a pose actually happened. */}
        {installations.some((i) => i.status === 'Completed' || i.status === 'InProgress') && (
          <div>
            <button
              type="button"
              data-testid="planning-add-intervention-btn"
              disabled={!can(PERMISSIONS.PLANNING_MANAGE)}
              title={reasonIfDenied(PERMISSIONS.PLANNING_MANAGE)}
              onClick={() => setAddInterventionOpen(true)}
              className="rounded-md border px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Planifier une intervention
            </button>
          </div>
        )}
        <AddInterventionDialog
          open={addInterventionOpen}
          onOpenChange={setAddInterventionOpen}
          projectId={projectId}
          onCreated={() => { void load(); onMutated() }}
        />

        {/* « Annuler le RDV » now lives discreetly on each section's Date row (Round 5) — distinct from
             « replanifier ». The terminal work-item Cancel is reserved for the project-cancellation flow.
             « Ouvrir le chantier » a été déplacé dans l'entête (lien nouvel onglet) — plus de bouton ici. */}

        <div ref={sectionRefs.Rapport}>
          <HistoryAccordion projectId={projectId} userNameById={userNameById} />
        </div>
      </div>
    </div>
  )
}

// ── Métré section ─────────────────────────────────────────────────────────
function MetreSection({
  projectId, onMutated, survey, alerts, surveyEvent, surveyEventPast, slots,
  employees, userNameById, form, onOpenForm, onSchedule, onReschedule, onCancelEvent, onConfirm, onUnconfirm,
  onReport, onEstimate, onNotApplicable, onReactivate, onAssign, onUnassign, onSetSlotNote, onFocusSlot,
}: {
  projectId: string
  onMutated: () => void
  survey: SurveyDto
  alerts: ProjectDetailAlertDto[]
  /** The LAST live créneau — kept for the closure block and the recap, not for the gestures. */
  surveyEvent: ScheduleEventDto | undefined
  surveyEventPast: boolean
  /** SOLY-PLAN-006 MS-04 — every live créneau of this métré, chronological. */
  slots: ScheduleEventDto[]
  employees: EmployeeDto[]
  userNameById: Record<string, string>
  form: MetreFormState
  onOpenForm: (f: MetreFormState) => void
  onSchedule: (body: CreateEventBody) => Promise<void>
  onReschedule: (eventId: string, body: RescheduleBody) => Promise<void>
  onCancelEvent: (eventId: string, reason: string) => Promise<void>
  onConfirm: (eventId: string, channel: ConfirmationChannel, note: string | undefined) => Promise<void>
  onUnconfirm: (eventId: string) => Promise<void>
  onReport: (summary: string) => Promise<void>
  onEstimate: (hours: number, reason: string | undefined) => Promise<void>
  onNotApplicable: (reason: string) => Promise<void>
  onReactivate: () => Promise<void>
  onAssign: (eventId: string, uid: string) => void
  onUnassign: (eventId: string, uid: string) => void
  onSetSlotNote: (eventId: string, note: string | null) => Promise<void>
  onFocusSlot?: (slot: ScheduleEventDto) => void
}) {
  const { can, reasonIfDenied } = usePermissions()
  const readOnly = !can(PERMISSIONS.PLANNING_MANAGE)
  const deniedHint = reasonIfDenied(PERMISSIONS.PLANNING_MANAGE)
  // `hasUpcomingSlot` n'est passé QUE par le métré : la pose ne peut pas atteindre l'état (son
  // rapport la ferme, `poseAcceptsNewSlot` refuse), donc son badge reste identique à l'octet près.
  const status = deriveSectionStatus({ status: survey.status, alerts, ready: survey.readyCheck.ready, missing: survey.readyCheck.missing, hasEvent: !!surveyEvent, eventPast: surveyEventPast, hasReport: survey.reportSummary != null, hasUpcomingSlot: hasUpcomingSlot(slots), kind: 'survey' })
  const sectionForm = form?.kind === 'section' ? form.form : null
  const close = () => onOpenForm(null)

  // 🔴 SOLY-PLAN-006 MS-04 — this is NOT `status !== Completed && …` any more, and the difference is
  // a visible bug, not a nicety. Décision D3 lets a métré take another créneau after its report, so
  // a reported métré must keep showing its passages AND the gesture to add one. Written the old
  // way, the door the backend just opened stayed invisible: the section rendered nothing but the
  // report.
  //
  // The pose keeps the three-terminal-state reading (its report closes the job), so the two are
  // deliberately different — and the shared half now lives in `isWorkItemSettled`, because the
  // comment on `isConfirmationLive` warned this rule lived in three places that agreed « by review,
  // not by construction ». This lot is where they stop agreeing.
  const isOpen = !isWorkItemSettled('TechnicalSurvey', survey.status)
  const surveyorNames = surveyEvent?.assignments.map((a) => userNameById[a.assignedUserId] ?? 'Employé').join(', ')
  // C4: the « non nécessaire » terminal block already states it — drop the redundant « Pas de métré » badge.
  const badgeLabel = survey.status === 'NotApplicable' ? null : status.label
  // The report-missing / report alerts (#4) are surfaced by <ReportArea>, not the bullet list — avoid the duplicate.
  const pointAlerts = alerts.filter((a) => a.anchorStep !== 'Rapport')
  // …and it is that same M6 alert that decides when the « Rapport manquant » block comes BACK on a
  // métré that already has one (SOLY-PLAN-006 MS-04 AC 5). The front does not recompute « ce
  // passage est-il couvert ? » — that rule lives in the engine, and it already has three homes.
  const reportMissingSignalled = alerts.some((a) => a.anchorStep === 'Rapport')
  const surveyDateLabel = surveyEvent ? formatCalendarDay(surveyEvent.startDate) : undefined

  // Which card shows its gestures — same rule as the pose: a lone créneau opens expanded, so
  // confirming a nominal métré still costs ONE tap; from two on they start collapsed.
  const [toggledSlotId, setToggledSlotId] = useState<string | null | undefined>(undefined)
  // A remembered id can outlive its créneau (cancel the open one and the state still points at it).
  // An id nobody owns means « no choice made », so the default applies again.
  const toggled = toggledSlotId != null && !slots.some((s) => s.id === toggledSlotId) ? undefined : toggledSlotId
  const expandedSlotId = form?.kind === 'slot'
    ? form.slotId
    : toggled === undefined ? (slots.length === 1 ? slots[0].id : null) : toggled

  return (
    <SectionCard testId="planning-section-metre" statusTestId="planning-section-metre-status"
      title="Métré" tone={status.tone} statusLabel={badgeLabel} count={status.count}>
      {/* « Non nécessaire » → clear terminal line + a way back (réactiver). */}
      {survey.status === 'NotApplicable' && (
        <TerminalNotApplicable area="metre" kind="survey" reason={survey.notApplicableReason} onReactivate={onReactivate} readOnly={readOnly} />
      )}
      {survey.status === 'Cancelled' && (
        <p className="text-xs text-muted-foreground">{survey.cancelReason ? `Métré annulé · ${survey.cancelReason}` : 'Métré annulé.'}</p>
      )}

      {/* Phase « terminé » : récap lecture seule du dernier passage. Il n'est plus exclusif de la
          liste — un métré rapporté peut avoir un passage de plus, et le récap parle du dernier. */}
      {survey.status === 'Completed' && slots.length === 0 && <CompletedRecap area="metre" event={surveyEvent} who={surveyorNames} />}

      {/* Rapport — EN HAUT (mini-scope 29) : rapport « Effectué » OU bloc « clôture » primaire (un seul CTA). */}
      <ReportArea
        area="metre" status={survey.status} reportSummary={survey.reportSummary} reportSummaryAt={survey.reportSummaryAt}
        eventPast={surveyEventPast} eventDateLabel={surveyDateLabel} reportMissingSignalled={reportMissingSignalled}
        onOpenReport={() => onOpenForm({ kind: 'section', form: 'report' })} readOnly={readOnly} />

      {isOpen && (
        <div className="divide-y divide-border/60">
          <FieldRow label="Estimation" defined value={`${survey.estimatedDurationHours} h`}
            action={readOnly ? undefined : { label: 'modifier', testId: 'planning-metre-estimate-btn', onClick: () => onOpenForm({ kind: 'section', form: 'estimate' }) }} />
          {slots.length === 0 ? (
            <FieldRow label="Date" defined={false} value={null} undefinedLabel="non planifié"
              action={readOnly ? undefined : { label: 'planifier', testId: 'planning-metre-schedule-btn', onClick: () => onOpenForm({ kind: 'section', form: 'schedule' }) }} />
          ) : (
            /* SOLY-PLAN-006 MS-04 — un métré est un ENSEMBLE de passages, chacun avec ses gestes.
               La grammaire est celle déjà en production sur la pose, reprise telle quelle : à UN
               créneau, ni en-tête « Créneaux » ni « 1/1 », carte ouverte — le cas dominant ne paie
               rien (principe directeur de PLAN-003). Rendre N passages en lignes-champ aurait
               inventé une 3ᵉ grammaire que la session UX devrait ensuite défaire. */
            <div className="px-1 py-1.5" data-testid="planning-metre-slots">
              {slots.length > 1 && (
                <div className="mb-1 text-[11px] uppercase text-muted-foreground">Créneaux</div>
              )}
              <ul className="space-y-1">
                {slots.map((s, i) => (
                  <WorkItemSlotCard
                    key={s.id} slot={s} area="metre" kind="TechnicalSurvey" index={i} total={slots.length}
                    projectId={projectId} technicalSurveyId={survey.id}
                    employees={employees} userNameById={userNameById}
                    phaseSuffix=""
                    readOnly={readOnly}
                    expanded={expandedSlotId === s.id}
                    onToggle={() => {
                      // Any tap on a card closes the open créneau form — including a tap on ANOTHER
                      // card, which otherwise looked like a dead control (the open form pins its own
                      // card expanded and wins over the toggle below).
                      if (form?.kind === 'slot') onOpenForm(null)
                      // Toggle against what is ON SCREEN, not against the remembered id: a lone
                      // créneau renders expanded while the memory still says « nothing chosen ».
                      setToggledSlotId(expandedSlotId === s.id ? null : s.id)
                    }}
                    form={form?.kind === 'slot' && form.slotId === s.id ? form.form : null}
                    onOpenForm={(f) => onOpenForm(f ? { kind: 'slot', slotId: s.id, form: f } : null)}
                    onReschedule={onReschedule} onCancelEvent={onCancelEvent}
                    onConfirm={onConfirm} onUnconfirm={onUnconfirm}
                    onAssign={onAssign} onUnassign={onUnassign}
                    onSetNote={onSetSlotNote}
                    onFocusSlot={onFocusSlot}
                  />
                ))}
              </ul>
              {/* Discreet, never a primary button — one passage is the default and adding one is a
                  gesture. 🔴 Still offered on a REPORTED métré (décision D3) : le geste ne se ferme
                  que sur « annulé » / « non nécessaire », et là l'utilisateur lit pourquoi (bloc
                  terminal au-dessus). Le prédicat miroite le domaine, il ne le réinvente pas. */}
              {surveyAcceptsNewSlot(survey) && (
                <button type="button" data-testid="planning-metre-add-slot"
                  onClick={() => onOpenForm({ kind: 'section', form: 'add-slot' })}
                  disabled={readOnly} title={deniedHint}
                  className="mt-1.5 text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50">
                  + Ajouter un créneau
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Alerts — les points à traiter (le « rapport manquant » #4 est porté par <ReportArea>, pas ici). */}
      {pointAlerts.length > 0 && (
        <ul className="mt-2 space-y-1">
          {pointAlerts.map((a) => (
            <li key={a.key} className="text-xs">
              <span className="font-medium text-foreground">{a.title}</span>
              {a.context && <span className="block text-muted-foreground">{a.context}</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Section-level inline forms (one at a time). Everything acting on ONE créneau — confirmer,
          annuler, métreur, replanifier, note — lives in its card. */}
      {sectionForm === 'schedule' && (
        <ScheduleForm area="metre" kind="TechnicalSurvey" projectId={projectId} technicalSurveyId={survey.id}
          employees={employees} initialUnit="Hours" initialValue={survey.estimatedDurationHours}
          onSubmit={onSchedule} onCancel={close} />
      )}
      {/* One MORE passage: same form, starting from a BLANK date and the last crew. The date is
          precisely what is not known yet — prefilling today would place work on a day nobody chose.
          The duration starts at the ESTIMATE, which is what the estimate means since MS-02: how long
          ONE visit takes. Not a remainder — a métré has no budget (décision D2). */}
      {sectionForm === 'add-slot' && (
        <ScheduleForm area="metre" kind="TechnicalSurvey" projectId={projectId} technicalSurveyId={survey.id}
          employees={employees} initialUnit="Hours" initialValue={survey.estimatedDurationHours}
          initialDayIso=""
          initialAssignees={assignableOnly(slots.at(-1)?.assignments.map((a) => a.assignedUserId) ?? [], employees)}
          onSubmit={onSchedule} onCancel={close} />
      )}
      {/* SOLY-PLAN-006 MS-07 — the form opens on the existing text. Without this, bringing the
          « Rapport manquant » block back on a reported métré would erase the first passage's report
          on save, silently. */}
      {sectionForm === 'report' && <ReportForm area="metre" initialSummary={survey.reportSummary} onSubmit={onReport} onCancel={close} />}
      {sectionForm === 'estimate' && (
        <EstimateForm kind="survey" area="metre" initialHours={survey.estimatedDurationHours}
          onSubmit={({ hours, reason }) => onEstimate(hours, reason)} onCancel={close} />
      )}
      {sectionForm === 'na' && <ReasonForm action="na" area="metre" onSubmit={onNotApplicable} onCancel={close} />}

      {/* Détail « préparation » : ready-check (toujours la liste complète) + délais. Le ready-check vit
          en phase A ; les « Délais » disparaissent une fois tout prêt (mini-scope 29) SAUF si une règle
          à seuil alerte encore — M2/M3 sont orthogonales au ready-check et leur sortie « Pas un souci
          ici » doit rester atteignable depuis l'alerte qui la pointe (review #1). */}
      {isOpen && (!surveyEventPast || sectionHasConstraintAlert(alerts)) && (
        <div className="mt-2 border-t pt-2" data-testid="planning-section-metre-detail">
          {!surveyEventPast && <ReadyCheckDetail kind="survey" missing={survey.readyCheck.missing} />}
          {(!survey.readyCheck.ready || sectionHasConstraintAlert(alerts)) && (
            <ConstraintsBlock readOnly={!can(PERMISSIONS.PLANNING_MANAGE)} projectId={projectId} section="metre" alerts={alerts} onMutated={onMutated} />
          )}
        </div>
      )}

      {/* Lifecycle actions — « Terminer » vit désormais dans le bloc « clôture » (haut). Reste « non nécessaire ».
          🔴 Gardé par `surveyAcceptsNotApplicable`, PAS par `isOpen` : D3 a ouvert la porte des
          CRÉNEAUX sur un métré rapporté, pas celle des transitions de cycle de vie.
          `MarkNotApplicable` refuse toujours un `Completed`, et offrir le geste revenait à faire
          répondre au backend « Cannot mark NotApplicable a TechnicalSurvey in status Completed ». */}
      {form === null && surveyAcceptsNotApplicable(survey) && (
        <div className="mt-2">
          <button type="button" data-testid="planning-survey-na-btn" onClick={() => onOpenForm({ kind: 'section', form: 'na' })}
            disabled={readOnly} title={deniedHint}
            className="text-[11px] text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50">
            Métré non nécessaire
          </button>
        </div>
      )}
    </SectionCard>
  )
}

// ── Pose section ──────────────────────────────────────────────────────────
function PoseSection({
  projectId, onMutated, installation, alerts, installEventPast, installEvent, slots, nature, unreceivedLineCount, userNameById, employees,
  form, onOpenForm, onSchedule, onReschedule, onCancelEvent, onConfirm, onUnconfirm, onReport, onEstimate, onNotApplicable, onReactivate, onNotes, onAssign, onUnassign, onSetSlotNote, onFocusSlot,
}: {
  projectId: string
  onMutated: () => void
  installation: InstallationDto
  alerts: ProjectDetailAlertDto[]
  installEventPast: boolean
  installEvent: ProjectDetailDto['events'][number] | undefined
  /** SOLY-PLAN-003 MS-09 — every live créneau of this pose, chronological. */
  slots: ProjectDetailDto['events'][number][]
  /** Why this pose exists (SOLY-FINITION-001) — badge next to the header, nothing for Initial. */
  nature: InterventionNature
  /** Supplier lines still expected on the project — the closure reconciliation prompt. */
  unreceivedLineCount: number
  userNameById: Record<string, string>
  employees: EmployeeDto[]
  form: PoseFormState
  onOpenForm: (f: PoseFormState) => void
  onSchedule: (body: CreateEventBody) => Promise<void>
  /* Every créneau-scoped callback takes the event id since MS-11: the section no longer has « the »
     event to close over, it has a list. */
  onReschedule: (eventId: string, body: RescheduleBody) => Promise<void>
  onCancelEvent: (eventId: string, reason: string) => Promise<void>
  onConfirm: (eventId: string, channel: ConfirmationChannel, note: string | undefined) => Promise<void>
  onUnconfirm: (eventId: string) => Promise<void>
  onReport: (summary: string) => Promise<void>
  onEstimate: (body: { unit: 'Hours' | 'Days'; value: number; resources: number; reason?: string }) => Promise<void>
  onNotApplicable: (reason: string) => Promise<void>
  onReactivate: () => Promise<void>
  onNotes: (notes: string) => Promise<void>
  onAssign: (eventId: string, uid: string) => void
  onUnassign: (eventId: string, uid: string) => void
  /** Writes ONE créneau's note — « ce qu'on fait dans ce bloc » (SOLY-PLAN-004). */
  onSetSlotNote: (eventId: string, note: string | null) => Promise<void>
  /** Jump the agenda to a créneau (MS-12). Omitted → the gesture is not offered. */
  onFocusSlot?: (slot: ScheduleEventDto) => void
}) {
  // The estimate is a budget the créneaux consume (MS-05) — same arithmetic as the fiche, one helper.
  const budget = poseBudget(installation, slots)
  const status = deriveSectionStatus({ status: installation.status, alerts, ready: installation.readyCheck.ready, missing: installation.readyCheck.missing, hasEvent: !!installEvent, eventPast: installEventPast, hasReport: installation.reportSummary != null, partiallyPlanned: budget.slotCount > 0 && budget.remainingHours > 0, kind: 'installation' })
  // AC6 / F3: the upstream blockers (métré, commandes) are surfaced by the always-visible ready-check
  // detail below — no separate hint block. The « En attente » status carries the at-a-glance signal,
  // and the pose stays planifiable ahead of the métré (the BE imposes no constraint).
  // Reads the SAME predicate as the métré section and the agenda bar (SOLY-PLAN-006 MS-04). The
  // answer differs — a reported pose IS settled, a reported métré is not — but the rule now lives in
  // one place instead of the three that « agreed by review, not by construction ».
  const isOpen = !isWorkItemSettled('Installation', installation.status)
  const close = () => onOpenForm(null)
  const sectionForm = form?.kind === 'section' ? form.form : null
  // Which card shows its gestures. `undefined` = the user has not chosen yet → a lone créneau opens
  // expanded, so confirming a nominal pose still costs ONE tap (it used to be a plain row with a
  // button); from two créneaux on they start collapsed, which is the point of a list.
  // A card whose form is open is always expanded, whatever the user last toggled.
  const [toggledSlotId, setToggledSlotId] = useState<string | null | undefined>(undefined)
  // A remembered id can outlive its créneau — cancel the one that was open and the state still
  // points at it. Left as-is, no card matches, and a pose back down to ONE créneau would stay
  // collapsed: the same screen would be expanded or not depending on how the user got there.
  // An id nobody owns means « no choice made », so the default applies again.
  const toggled = toggledSlotId != null && !slots.some((s) => s.id === toggledSlotId) ? undefined : toggledSlotId
  const expandedSlotId = form?.kind === 'slot'
    ? form.slotId
    : toggled === undefined ? (slots.length === 1 ? slots[0].id : null) : toggled
  const unitFr = installation.estimatedDurationUnit === 'Days' ? 'j' : 'h'
  const placeurCount = installation.estimatedResourceCount
  // « Non estimé » is a real answer since SOLY-PLAN-004 — a pose is no longer born at 1 j / 2
  // placeurs. Rendering « null j · null placeur » is what a naive template would have done.
  const estimationLabel = installation.estimatedDurationValue === null || placeurCount === null
    ? 'non estimé'
    : `${installation.estimatedDurationValue} ${unitFr} · ${placeurCount} placeur${placeurCount > 1 ? 's' : ''}`
  const { can, reasonIfDenied } = usePermissions()
  const readOnly = !can(PERMISSIONS.PLANNING_MANAGE)
  const deniedHint = reasonIfDenied(PERMISSIONS.PLANNING_MANAGE)
  const installerNames = installEvent?.assignments.map((a) => userNameById[a.assignedUserId] ?? 'Employé').join(', ')
  // C4: drop the redundant « Pas de pose » badge — the terminal block states it.
  const badgeLabel = installation.status === 'NotApplicable' ? null : status.label
  // The report-missing / report alert (#13) is surfaced by <ReportArea>, not the bullet list.
  const pointAlerts = alerts.filter((a) => a.anchorStep !== 'Rapport')
  const poseDateLabel = installEvent ? formatCalendarDay(installEvent.startDate) : undefined

  // Phase 1 keeps the historical testids (TC-019 & friends); later phases get suffixed ones
  // so a multi-pose drawer never emits duplicate testids (Playwright strict mode).
  const phaseSuffix = installation.phase === 1 ? '' : `-${installation.phase}`
  return (
    <SectionCard testId={`planning-section-pose${phaseSuffix}`} statusTestId={`planning-section-pose-status${phaseSuffix}`}
      title={installation.phase === 1 ? 'Pose' : `Pose · intervention ${installation.phase}`}
      titleBadge={<InterventionNatureBadge nature={nature} />}
      tone={status.tone} statusLabel={badgeLabel} count={status.count}>
      {/* « Non nécessaire » → clear terminal line + a way back (réactiver). */}
      {installation.status === 'NotApplicable' && (
        <TerminalNotApplicable area="pose" kind="installation" reason={installation.notApplicableReason} onReactivate={onReactivate} readOnly={readOnly} />
      )}
      {installation.status === 'Cancelled' && (
        <p className="text-xs text-muted-foreground">{installation.cancelReason ? `Pose annulée · ${installation.cancelReason}` : 'Pose annulée.'}</p>
      )}

      {/* Phase « terminé » (Completed) : récap lecture seule + rapport (rendu par <ReportArea> ci-dessous). */}
      {installation.status === 'Completed' && <CompletedRecap area="pose" event={installEvent} who={installerNames} />}

      {/* Rapport — EN HAUT (mini-scope 29) : rapport « Effectuée » OU bloc « clôture » primaire (un seul CTA). */}
      <ReportArea
        area="pose" status={installation.status} reportSummary={installation.reportSummary} reportSummaryAt={installation.reportSummaryAt}
        eventPast={installEventPast} eventDateLabel={poseDateLabel} onOpenReport={() => onOpenForm({ kind: 'section', form: 'report' })}
        readOnly={readOnly}
        unreceivedLines={unreceivedLineCount > 0 ? { count: unreceivedLineCount, href: `/planning/projects/${projectId}/commandes` } : undefined} />

      {/* Active → field rows. Same rule as the métré above: every créneau of an OPEN pose is
          confirmable whatever its date, and the whole list disappears when the section closes. */}
      {isOpen && (
        <div className="divide-y divide-border/60">
          <FieldRow label="Estimation" defined value={estimationLabel}
            action={readOnly ? undefined : { label: 'modifier', testId: 'planning-pose-estimate-btn', onClick: () => onOpenForm({ kind: 'section', form: 'estimate' }) }} />
          {slots.length === 0 ? (
            <FieldRow label="Date" defined={false} value={null} undefinedLabel="non planifié"
              action={readOnly ? undefined : { label: 'planifier', testId: 'planning-pose-schedule-btn', onClick: () => onOpenForm({ kind: 'section', form: 'schedule' }) }} />
          ) : (
            /* SOLY-PLAN-003 MS-11 — a pose is a SET of créneaux, and each one carries its own
               gestures. MS-09 listed them read-only, betting the agenda fiche would be the place to
               compose them; the cockpit turned out to be the surface actually used, and the
               read-only list was not neutral — `Placeurs`, `Date` and `Confirmation` lived in the
               « exactly one créneau » branch, so a pose in two blocks lost all three.
               One grammar from 1 to N: at a single créneau neither the « Créneaux » header nor the
               « 1/1 » ordinality shows, and the card opens expanded so confirming a nominal pose
               still costs exactly one tap. */
            <div className="px-1 py-1.5" data-testid="planning-pose-slots">
              {slots.length > 1 && (
                <div className="mb-1 text-[11px] uppercase text-muted-foreground">Créneaux</div>
              )}
              <ul className="space-y-1">
                {slots.map((s, i) => (
                  <WorkItemSlotCard
                    key={s.id} slot={s} area="pose" kind="Installation" index={i} total={slots.length}
                    projectId={projectId} installationId={installation.id}
                    expectedResources={placeurCount ?? undefined} employees={employees} userNameById={userNameById}
                    phaseSuffix={phaseSuffix}
                    readOnly={readOnly}
                    expanded={expandedSlotId === s.id}
                    onToggle={() => {
                      // Any tap on a card closes the open créneau form — including a tap on ANOTHER
                      // card. Without this the open form pins its own card expanded (it wins over
                      // the toggle below), so tapping a neighbour looked like a dead control.
                      if (form?.kind === 'slot') onOpenForm(null)
                      // Toggle against what is ON SCREEN, not against the remembered id. A lone
                      // créneau renders expanded while the memory still says « nothing chosen », so
                      // comparing to the memory made the first tap re-expand an already-open card:
                      // it took two clicks to close what the chevron said was open.
                      setToggledSlotId(expandedSlotId === s.id ? null : s.id)
                    }}
                    form={form?.kind === 'slot' && form.slotId === s.id ? form.form : null}
                    onOpenForm={(f) => onOpenForm(f ? { kind: 'slot', slotId: s.id, form: f } : null)}
                    onReschedule={onReschedule} onCancelEvent={onCancelEvent}
                    onConfirm={onConfirm} onUnconfirm={onUnconfirm}
                    onAssign={onAssign} onUnassign={onUnassign}
                    onSetNote={onSetSlotNote}
                    onFocusSlot={onFocusSlot}
                  />
                ))}
              </ul>
              {/* Discreet, never a primary button — one créneau is the default and adding one is a
                  gesture (décision 6, same language as the agenda fiche). Hidden once the report
                  closes the pose: the UI mirrors the domain refusal of MS-07 instead of offering
                  something the backend would reject. */}
              {poseAcceptsNewSlot(installation) ? (
                <button type="button" data-testid={`planning-pose${phaseSuffix}-add-slot`}
                  onClick={() => onOpenForm({ kind: 'section', form: 'add-slot' })}
                  disabled={readOnly} title={deniedHint}
                  className="mt-1.5 text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50">
                  + Ajouter un créneau
                </button>
              ) : (
                <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`planning-pose${phaseSuffix}-slot-closed`}>
                  Rapport saisi — la pose est clôturée. Créez une finition depuis le chantier.
                </p>
              )}
            </div>
          )}
          {/* AC 4 — the wording must say what is LEFT, never « pas planifié » on a chantier where
              two days are already booked. That message is what made scénario S4 disappear.
              INFORMATIVE, with no action: this form reschedules the existing créneau when there is
              one (`reschedule={installEvent ? …}`), so a « planifier » button here would move the
              day already agreed with the client instead of adding one. Composing créneaux is the
              fiche's job — one surface, one gesture. */}
          {budget.slotCount > 0 && budget.remainingHours > 0 && (
            <FieldRow label="Reste à planifier" defined={false} value={null}
              testId="planning-pose-remaining"
              undefinedLabel={formatDuration(budget.remainingHours, installation.estimatedDurationUnit)} />
          )}
          <FieldRow label="Notes" optional defined={!!installation.schedulingNotes}
            value={installation.schedulingNotes} undefinedLabel="aucune"
            action={readOnly ? undefined : { label: installation.schedulingNotes ? 'modifier' : 'ajouter', testId: 'planning-pose-notes-btn', onClick: () => onOpenForm({ kind: 'section', form: 'notes' }) }} />
        </div>
      )}

      {/* Alerts — les points à traiter (le « rapport manquant » #13 est porté par <ReportArea>, pas ici). */}
      {pointAlerts.length > 0 && (
        <ul className="mt-2 space-y-1">
          {pointAlerts.map((a) => (
            <li key={a.key} className="text-xs">
              <span className="font-medium text-foreground">{a.title}</span>
              {a.context && <span className="block text-muted-foreground">{a.context}</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Section-level inline forms (one at a time). Everything that acts on ONE créneau —
          confirmer, annuler, placeurs, replanifier — moved into its card (MS-11). */}
      {sectionForm === 'schedule' && (
        /* A first créneau starts at ONE unit when the pose carries no estimate: this form places a
           block, and « rien d'estimé » is not a duration to place (SOLY-PLAN-004). */
        <ScheduleForm area="pose" kind="Installation" projectId={projectId} installationId={installation.id}
          employees={employees} initialUnit={installation.estimatedDurationUnit}
          initialValue={installation.estimatedDurationValue ?? 1}
          expectedResources={installation.estimatedResourceCount ?? undefined}
          onSubmit={(body) => onSchedule(body)} onCancel={close} />
      )}
      {/* One MORE créneau: same form, but starting from a BLANK date and the last crew. The date is
          precisely what is not known yet (scénario S4) and prefilling today would place work on a
          day nobody chose; the duration starts at one unit rather than the remaining budget, for
          the same reason. */}
      {/* L'équipe reprise du dernier créneau passe par `assignableOnly` : sans ce filtre, un membre
          parti depuis restait dans le Set soumis SANS avoir de puce à l'écran — le picker ne liste
          que les actifs — donc invisible et indésélectionnable, et le serveur refusait
          ASSIGNEE_NOT_ASSIGNABLE sans que l'utilisateur puisse rien y faire. */}
      {sectionForm === 'add-slot' && (
        <ScheduleForm area="pose" kind="Installation" projectId={projectId} installationId={installation.id}
          employees={employees} initialUnit={installation.estimatedDurationUnit} initialValue={1}
          expectedResources={installation.estimatedResourceCount ?? undefined}
          initialDayIso=""
          initialAssignees={assignableOnly(slots.at(-1)?.assignments.map((a) => a.assignedUserId) ?? [], employees)}
          onSubmit={(body) => onSchedule(body)} onCancel={close} />
      )}
      {sectionForm === 'report' && <ReportForm area="pose" onSubmit={onReport} onCancel={close} />}
      {sectionForm === 'estimate' && (
        <EstimateForm kind="installation" area="pose"
          initialUnit={installation.estimatedDurationUnit} initialValue={installation.estimatedDurationValue}
          initialResources={installation.estimatedResourceCount}
          scheduled={installEvent
            ? { unit: installEvent.durationUnit, confirmedAt: installEvent.confirmedAt ?? null }
            : undefined}
          onSubmit={onEstimate} onCancel={close} />
      )}
      {sectionForm === 'na' && <ReasonForm action="na" area="pose" onSubmit={onNotApplicable} onCancel={close} />}
      {sectionForm === 'notes' && <NotesForm area="pose" initialNotes={installation.schedulingNotes ?? ''} onSubmit={onNotes} onCancel={close} />}

      {/* Détail « préparation » : ready-check + délais — même règle que le Métré : les « Délais »
          restent atteignables tant qu'une règle à seuil alerte (sortie dismiss, review #1). */}
      {isOpen && (!installEventPast || sectionHasConstraintAlert(alerts)) && (
        <div className="mt-2 border-t pt-2" data-testid="planning-section-pose-detail">
          {!installEventPast && <ReadyCheckDetail kind="installation" missing={installation.readyCheck.missing} />}
          {(!installation.readyCheck.ready || sectionHasConstraintAlert(alerts)) && (
            <ConstraintsBlock readOnly={!can(PERMISSIONS.PLANNING_MANAGE)} projectId={projectId} section="pose" alerts={alerts} onMutated={onMutated} />
          )}
        </div>
      )}

      {/* Lifecycle actions — alignées sur le Métré : « Terminer » vit dans le bloc « clôture » (haut),
          reste l'échappatoire « non nécessaire ». Le bouton « Démarrer » a été retiré (mini-scope 29) ;
          l'endpoint InProgress côté backend est conservé, simplement plus déclenché par le cockpit. */}
      {form === null && isOpen && (
        <div className="mt-2">
          <button type="button" data-testid="planning-install-na-btn" onClick={() => onOpenForm({ kind: 'section', form: 'na' })}
            disabled={readOnly} title={deniedHint}
            className="text-[11px] text-muted-foreground hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50">
            Pose non nécessaire
          </button>
        </div>
      )}
    </SectionCard>
  )
}
